import { AcceptanceState } from "@/types/enums/AcceptanceState";

/**
 * Which built-in guest fields the backend refuses on the UPDATE path.
 *
 * `main-backend GuestService.validateGuestTypeUpdate` fails EVERY `guestType`
 * change with "Changing guestType is not supported" — both directions, no
 * exceptions, since commit fc54e740 (2026-07-15). The cockpit nevertheless
 * kept offering an editable "Gast Typ" dropdown, in the default detail layout
 * and as an inline grid column, so the control could only ever produce a 400.
 * (Before that commit the backend applied the field BLIND, which is how rows
 * ended up as `{guestType:"mainGuest", mainGuest:<old parent>}` — a shape no
 * API can repair, because `mainGuest` is not clearable.)
 *
 * CREATE is a different question and stays fully editable: choosing the type
 * of a guest that does not exist yet is the one place the choice is real.
 * Note this deliberately does NOT flip `FIELDS_GUEST.guestType.editable` —
 * that flag is read by `TCCSingleSelect`, which short-circuits to read-only
 * BEFORE the editing branch, so setting it false would collapse the create
 * dialog's dropdown too.
 *
 * WAVE 3 implements real companion↔mainGuest conversion in the backend. When
 * it ships, flip {@link GUEST_TYPE_UPDATE_SUPPORTED} and every gate below
 * follows — nothing has to be found and deleted.
 *
 * <p>Status 2026-08-14: FLIPPED. The backend half is live in production —
 * `dc38ced6` (guest-type conversion as one authority, 2026-08-12) is deployed,
 * and `GuestService.validateGuestTypeUpdate` now allows both directions.
 *
 * <p>How that was established, because the obvious method gives the wrong
 * answer: the newest tag is `v1.3.49` (2026-08-08) and main-backend's workflow
 * deploys on tag, so reading the tags says the conversion is unshipped. It is
 * not — production runs code newer than any tag. Measured instead, against
 * production, with a control:
 * <pre>
 *   /api/v1/events/{id}/definitely-not-a-real-route  -> 404   (control)
 *   /api/v1/nonsense-collection/xyz                  -> 404   (control)
 *   /api/v1/events/{id}/guest-quota                  -> 401   (route EXISTS)
 * </pre>
 * `guest-quota` was added by `daf8179e` on 2026-08-13, and `dc38ced6` is its
 * ancestor — so the conversion is deployed. Do not re-derive this from tags.
 *
 * <p>Two live backend defects sit next to this and are NOT gated by this flag —
 * they affect ordinary companion edits today, flipped or not. Named here so the
 * next reader does not rediscover them: `GuestService:2756` rejects a companion
 * whose row was loaded without `mainGuest` BEFORE the change-gated validator
 * runs, and `rejectIntraBatchParentChain` treats a row as "touched" when a field
 * is merely non-null rather than changed, so saving a parent together with its
 * companion can 400 the whole batch.
 *
 * <p>2026-08-14, same day: the flip alone was too coarse. It is field-wide, and
 * a guest whose request is still PENDING must keep its type — see
 * {@link isGuestTypeLockedByAcceptance}. This constant now answers only "does
 * the backend support conversion at all?"; "may THIS guest be converted?" is
 * the state gate below.
 */
export const GUEST_TYPE_UPDATE_SUPPORTED = true;

/** Built-in guest field names the update surfaces must not offer as editable. */
export const GUEST_UPDATE_BLOCKED_BUILTINS: ReadonlySet<string> = new Set(
  GUEST_TYPE_UPDATE_SUPPORTED ? [] : ["guestType"],
);

/**
 * The flag above answers "which FIELD?", never "which GUEST?" — and that is the
 * gap this closes.
 *
 * An UNDECIDED request may not be re-typed. The pending decision is about the
 * guest as it stands: converting a companion request into a main guest `$unset`s
 * the parent pointer in the same backend write (`mainGuest` is not clearable, so
 * nothing restores it), moves the parent's `numCompanions` rollup, and makes the
 * approval fire `REQUEST_APPROVED` where the organiser was promised
 * `COMPANION_APPROVED`. Reachable straight from the Anfragen page: a row there
 * opens the full detail popup, `guestType` is `PLACEABLE_BUILTIN_ORDER[0]`.
 *
 * Only PENDING locks. ACCEPTED / SYSTEM / DECLINED stay convertible — the lock
 * is about an open decision, not about the guest being a companion.
 *
 * Mirrored in `main-backend GuestService.validateGuestTypeUpdate`, which refuses
 * the same change with `guestTypeLockedWhilePending`; this side only decides
 * whether to OFFER the control.
 */
export function isGuestTypeLockedByAcceptance(
  acceptanceState?: AcceptanceState | string | null,
): boolean {
  return acceptanceState === AcceptanceState.PENDING;
}

/**
 * True when this built-in cannot be written on an existing guest. Custom
 * (additional) fields are never blocked — one legitimately named `guestType`
 * is a different field and keeps its own editor.
 *
 * `acceptanceState` is optional because the layout-BUILDER canvas renders cells
 * with no guest behind them at all. The cost is that a caller which forgets to
 * pass it fails OPEN rather than closed, which is why both real call sites
 * (`GuestLayoutFieldInput`, `isEditableFieldGuest`) carry their own pin.
 */
export function isGuestFieldBlockedOnUpdate(
  name: string,
  additionalField?: boolean,
  acceptanceState?: AcceptanceState | string | null,
): boolean {
  if (additionalField) return false;
  if (GUEST_UPDATE_BLOCKED_BUILTINS.has(name)) return true;
  return name === "guestType" && isGuestTypeLockedByAcceptance(acceptanceState);
}

/**
 * Why the control is read-only, so the caller can pick the right sentence.
 * `"pending"` → the state lock above; `"unsupported"` → the flag; `null` → not
 * blocked at all.
 */
export function guestTypeBlockReason(
  name: string,
  additionalField?: boolean,
  acceptanceState?: AcceptanceState | string | null,
): "unsupported" | "pending" | null {
  if (additionalField) return null;
  if (GUEST_UPDATE_BLOCKED_BUILTINS.has(name)) return "unsupported";
  if (name === "guestType" && isGuestTypeLockedByAcceptance(acceptanceState)) {
    return "pending";
  }
  return null;
}
